import _asyncToGenerator from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.15.4/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";
import _regeneratorRuntime from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.15.4/node_modules/@babel/runtime/regenerator";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppModal } from '@lib/common';
import { SpeakingMaxAttempts, TimeUnit } from '@module/form';
import { SpeakingOutOfAttemptsArea, SpeakingPermissionArea, SpeakingPreparationArea, SpeakingRecordedArea, SpeakingRecorderInitArea, SpeakingRecordingArea, SpeakingRecorderState, createSpeakingMediaRecorder, isSpeakingRecorderSupported, resolveSpeakingLabel, stopSpeakingMediaStream } from '../../common';
import { jsx as _jsx } from "react/jsx-runtime";
export var SpeakingAnswerRecorder = function SpeakingAnswerRecorder(_ref) {
  var settings = _ref.settings,
      label = _ref.label,
      onSubmit = _ref.onSubmit,
      onAttempt = _ref.onAttempt,
      _ref$attemptCount = _ref.attemptCount,
      attemptCount = _ref$attemptCount === void 0 ? 0 : _ref$attemptCount,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$isSubmitting = _ref.isSubmitting,
      isSubmitting = _ref$isSubmitting === void 0 ? false : _ref$isSubmitting,
      operation = _ref.operation;
  var resolvedLabel = resolveSpeakingLabel(label);
  var preparationTime = TimeUnit.toTotalSeconds(settings.preparationTime, settings.preparationTimeUnit);
  var speakingTime = TimeUnit.toTotalSeconds(settings.speakingTime, settings.speakingTimeUnit);
  var minimumDuration = settings.minAnswerDuration === -1 ? 0 : TimeUnit.toTotalSeconds(settings.minAnswerDuration, settings.minAnswerDurationUnit);
  var maxAttempts = settings.maxAttempts;
  var isUnlimitedAttempts = SpeakingMaxAttempts.isUnlimited(maxAttempts);

  var _useState = useState(SpeakingRecorderState.Init),
      recorderState = _useState[0],
      setRecorderState = _useState[1];

  var _useState2 = useState(preparationTime),
      preparationTimeLeft = _useState2[0],
      setPreparationTimeLeft = _useState2[1];

  var _useState3 = useState(speakingTime),
      speakingTimeLeft = _useState3[0],
      setSpeakingTimeLeft = _useState3[1];

  var _useState4 = useState(0),
      recordedDuration = _useState4[0],
      setRecordedDuration = _useState4[1];

  var _useState5 = useState(null),
      audioBlob = _useState5[0],
      setAudioBlob = _useState5[1];

  var _useState6 = useState(null),
      audioUrl = _useState6[0],
      setAudioUrl = _useState6[1];

  var _useState7 = useState(null),
      activeOperationId = _useState7[0],
      setActiveOperationId = _useState7[1];

  var _useState8 = useState('idle'),
      attemptReservationState = _useState8[0],
      setAttemptReservationState = _useState8[1];

  var mediaRecorderRef = useRef(null);
  var mediaStreamRef = useRef(null);
  var audioChunksRef = useRef([]);
  var isStartingRecordingRef = useRef(false);
  var preparationTimerRef = useRef(null);
  var speakingTimerRef = useRef(null);
  var stopStream = useCallback(function () {
    stopSpeakingMediaStream(mediaStreamRef.current);
    mediaStreamRef.current = null;
  }, []);
  useEffect(function () {
    var _mediaRecorderRef$cur;

    if (!disabled) return;
    if (preparationTimerRef.current) clearTimeout(preparationTimerRef.current);
    if (speakingTimerRef.current) clearTimeout(speakingTimerRef.current);
    if (((_mediaRecorderRef$cur = mediaRecorderRef.current) === null || _mediaRecorderRef$cur === void 0 ? void 0 : _mediaRecorderRef$cur.state) === 'recording') mediaRecorderRef.current.stop();
    isStartingRecordingRef.current = false;
    stopStream();
  }, [disabled, stopStream]);
  useEffect(function () {
    return function () {
      var _mediaRecorderRef$cur2;

      if (preparationTimerRef.current) clearTimeout(preparationTimerRef.current);
      if (speakingTimerRef.current) clearTimeout(speakingTimerRef.current);
      if (((_mediaRecorderRef$cur2 = mediaRecorderRef.current) === null || _mediaRecorderRef$cur2 === void 0 ? void 0 : _mediaRecorderRef$cur2.state) === 'recording') mediaRecorderRef.current.stop();
      isStartingRecordingRef.current = false;
      stopStream();
    };
  }, [stopStream]);
  useEffect(function () {
    return function () {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);
  useEffect(function () {
    if (!activeOperationId || (operation === null || operation === void 0 ? void 0 : operation.operationId) !== activeOperationId) return;

    if (operation.stage === 'attemptReserved') {
      setAttemptReservationState('reserved');
      return;
    }

    if (operation.stage === 'error' && [SpeakingRecorderState.Preparation, SpeakingRecorderState.Reserving].includes(recorderState)) {
      if (preparationTimerRef.current) clearTimeout(preparationTimerRef.current);
      stopStream();
      setActiveOperationId(null);
      setAttemptReservationState('idle');
      setRecorderState(SpeakingRecorderState.Init);
    }

    if (recorderState === SpeakingRecorderState.Submitting && operation.stage === 'error') {
      setActiveOperationId(null);
      setRecorderState(SpeakingRecorderState.Recorded);
    }
  }, [activeOperationId, operation, recorderState, stopStream]);
  var startMediaRecording = useCallback(function (stream) {
    var currentRecorder = mediaRecorderRef.current;
    var hasLiveAudioTrack = stream.getAudioTracks().some(function (track) {
      return track.readyState === 'live';
    });

    if (isStartingRecordingRef.current || currentRecorder !== null && currentRecorder.state !== 'inactive' || !stream.active || !hasLiveAudioTrack) {
      if (!stream.active || !hasLiveAudioTrack) {
        mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        stopStream();
        setRecorderState(SpeakingRecorderState.Unsupported);
      }

      return;
    }

    if (typeof MediaRecorder === 'undefined') {
      stopStream();
      setRecorderState(SpeakingRecorderState.Unsupported);
      return;
    }

    isStartingRecordingRef.current = true;
    audioChunksRef.current = [];
    var mediaRecorder;

    try {
      mediaRecorder = createSpeakingMediaRecorder(stream);

      mediaRecorder.ondataavailable = function (event) {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = function () {
        var _audioChunksRef$curre;

        var mimeType = mediaRecorder.mimeType || ((_audioChunksRef$curre = audioChunksRef.current[0]) === null || _audioChunksRef$curre === void 0 ? void 0 : _audioChunksRef$curre.type);
        var blob = new Blob(audioChunksRef.current, mimeType ? {
          type: mimeType
        } : undefined);
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        mediaRecorderRef.current = null;
        isStartingRecordingRef.current = false;
        setRecorderState(SpeakingRecorderState.Recorded);
        stopStream();
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      isStartingRecordingRef.current = false;
      setRecorderState(SpeakingRecorderState.Recording);
    } catch (_unused) {
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      isStartingRecordingRef.current = false;
      stopStream();
      setRecorderState(SpeakingRecorderState.Unsupported);
    }
  }, [stopStream]);

  var startPreparation = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var stream, operationId;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              if (!(!isUnlimitedAttempts && attemptCount >= maxAttempts)) {
                _context.next = 2;
                break;
              }

              return _context.abrupt("return");

            case 2:
              if (isSpeakingRecorderSupported()) {
                _context.next = 5;
                break;
              }

              setRecorderState(SpeakingRecorderState.Unsupported);
              return _context.abrupt("return");

            case 5:
              _context.prev = 5;
              setRecorderState(SpeakingRecorderState.Permission);
              _context.next = 9;
              return navigator.mediaDevices.getUserMedia({
                audio: true
              });

            case 9:
              stream = _context.sent;
              mediaStreamRef.current = stream;
              operationId = onAttempt === null || onAttempt === void 0 ? void 0 : onAttempt();

              if (operationId) {
                _context.next = 16;
                break;
              }

              stopStream();
              setRecorderState(SpeakingRecorderState.Init);
              return _context.abrupt("return");

            case 16:
              setActiveOperationId(operationId);
              setAttemptReservationState('pending');
              setPreparationTimeLeft(preparationTime);
              setRecorderState(SpeakingRecorderState.Preparation);
              _context.next = 26;
              break;

            case 22:
              _context.prev = 22;
              _context.t0 = _context["catch"](5);
              stopStream();
              setRecorderState(SpeakingRecorderState.PermissionDenied);

            case 26:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[5, 22]]);
    }));

    return function startPreparation() {
      return _ref2.apply(this, arguments);
    };
  }();

  useEffect(function () {
    if (recorderState === SpeakingRecorderState.Reserving) {
      if (attemptReservationState === 'reserved' && mediaStreamRef.current) {
        startMediaRecording(mediaStreamRef.current);
      }

      return;
    }

    if (recorderState !== SpeakingRecorderState.Preparation) return;

    if (preparationTimeLeft <= 0) {
      setRecorderState(SpeakingRecorderState.Reserving);
      return;
    }

    preparationTimerRef.current = setTimeout(function () {
      return setPreparationTimeLeft(function (value) {
        return value - 1;
      });
    }, 1000);
    return function () {
      if (preparationTimerRef.current) clearTimeout(preparationTimerRef.current);
    };
  }, [attemptReservationState, preparationTimeLeft, recorderState, startMediaRecording]);
  useEffect(function () {
    if (recorderState !== SpeakingRecorderState.Recording) return;

    if (speakingTimeLeft <= 0) {
      var _mediaRecorderRef$cur3;

      if (((_mediaRecorderRef$cur3 = mediaRecorderRef.current) === null || _mediaRecorderRef$cur3 === void 0 ? void 0 : _mediaRecorderRef$cur3.state) === 'recording') mediaRecorderRef.current.stop();
      return;
    }

    speakingTimerRef.current = setTimeout(function () {
      setSpeakingTimeLeft(function (value) {
        return value - 1;
      });
      setRecordedDuration(function (value) {
        return value + 1;
      });
    }, 1000);
    return function () {
      if (speakingTimerRef.current) clearTimeout(speakingTimerRef.current);
    };
  }, [recorderState, speakingTimeLeft]);

  var handleSkipPreparation = function handleSkipPreparation() {
    if (preparationTimerRef.current) clearTimeout(preparationTimerRef.current);
    setPreparationTimeLeft(0);
    setRecorderState(SpeakingRecorderState.Reserving);
  };

  var handleStopRecording = function handleStopRecording() {
    var _mediaRecorderRef$cur4;

    if (((_mediaRecorderRef$cur4 = mediaRecorderRef.current) === null || _mediaRecorderRef$cur4 === void 0 ? void 0 : _mediaRecorderRef$cur4.state) === 'recording') mediaRecorderRef.current.stop();
  };

  var handleConfirmReRecord = function handleConfirmReRecord() {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordedDuration(0);
    setSpeakingTimeLeft(speakingTime);
    setAttemptReservationState('idle');
    mediaRecorderRef.current = null;
    isStartingRecordingRef.current = false;
    setRecorderState(SpeakingRecorderState.Init);
  };

  var handleReRecord = function handleReRecord() {
    return AppModal.confirm({
      title: resolvedLabel.confirmReRecordTitle,
      content: resolvedLabel.confirmReRecordMessage,
      okText: resolvedLabel.confirm,
      cancelText: resolvedLabel.cancel,
      centered: true,
      onOk: handleConfirmReRecord
    });
  };

  var handleSubmit = function handleSubmit() {
    var _mediaRecorderRef$cur5;

    if (preparationTimerRef.current) clearTimeout(preparationTimerRef.current);
    if (speakingTimerRef.current) clearTimeout(speakingTimerRef.current);
    if (((_mediaRecorderRef$cur5 = mediaRecorderRef.current) === null || _mediaRecorderRef$cur5 === void 0 ? void 0 : _mediaRecorderRef$cur5.state) === 'recording') mediaRecorderRef.current.stop();
    stopStream();
    if (!audioBlob) return;
    var operationId = onSubmit({
      audioFile: {
        id: '',
        url: '',
        size: audioBlob.size,
        type: audioBlob.type
      },
      responseTime: recordedDuration
    }, audioBlob);
    if (!operationId) return;
    setActiveOperationId(operationId);
    setRecorderState(SpeakingRecorderState.Submitting);
  };

  var isOutOfAttempts = !isUnlimitedAttempts && attemptCount >= maxAttempts && ![SpeakingRecorderState.Preparation, SpeakingRecorderState.Reserving, SpeakingRecorderState.Recording, SpeakingRecorderState.Recorded, SpeakingRecorderState.Submitting].includes(recorderState);
  if (isOutOfAttempts) return /*#__PURE__*/_jsx(SpeakingOutOfAttemptsArea, {
    label: label
  });
  if (recorderState === SpeakingRecorderState.Permission) return /*#__PURE__*/_jsx(SpeakingPermissionArea, {
    label: label,
    disabled: disabled,
    state: "requesting"
  });
  if (recorderState === SpeakingRecorderState.Reserving) return /*#__PURE__*/_jsx(SpeakingPermissionArea, {
    label: label,
    disabled: disabled,
    state: "reserving"
  });
  if (recorderState === SpeakingRecorderState.PermissionDenied) return /*#__PURE__*/_jsx(SpeakingPermissionArea, {
    label: label,
    disabled: disabled,
    state: "denied",
    onRetry: startPreparation
  });
  if (recorderState === SpeakingRecorderState.Unsupported) return /*#__PURE__*/_jsx(SpeakingPermissionArea, {
    label: label,
    disabled: disabled,
    state: "unsupported"
  });
  if (recorderState === SpeakingRecorderState.Preparation) return /*#__PURE__*/_jsx(SpeakingPreparationArea, {
    label: label,
    disabled: disabled,
    timeLeft: preparationTimeLeft,
    totalSeconds: preparationTime,
    onSkip: handleSkipPreparation
  });
  if (recorderState === SpeakingRecorderState.Recording) return /*#__PURE__*/_jsx(SpeakingRecordingArea, {
    label: label,
    disabled: disabled,
    timeLeft: speakingTimeLeft,
    totalSeconds: speakingTime,
    recordedDuration: recordedDuration,
    minimumDuration: minimumDuration,
    onStop: handleStopRecording
  });
  if ((recorderState === SpeakingRecorderState.Recorded || recorderState === SpeakingRecorderState.Submitting) && audioUrl) return /*#__PURE__*/_jsx(SpeakingRecordedArea, {
    label: label,
    disabled: disabled,
    audioUrl: audioUrl,
    duration: recordedDuration,
    audioId: "speaking-answer-preview",
    onReRecord: handleReRecord,
    onSubmit: handleSubmit,
    canReRecord: isUnlimitedAttempts || attemptCount < maxAttempts,
    isSubmitting: isSubmitting || recorderState === SpeakingRecorderState.Submitting
  });
  return /*#__PURE__*/_jsx(SpeakingRecorderInitArea, {
    label: label,
    disabled: disabled,
    preparationSeconds: preparationTime,
    speakingSeconds: speakingTime,
    onStart: startPreparation
  });
};